import React from 'react';

import { cssMap, cx } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Pressable, type StackProps, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	type HeaderElement,
	type ParsedElement,
	type SectionElement,
} from '../../dynamic-layout-parser/types/dynamic-layout-section';

import { OneColumnLayout } from './section-layout/one-column-layout';

type FoldedStyleProps = {
	section: SectionElement;
	header: HeaderElement | undefined;
	content: ParsedElement[];
	children?: React.ReactNode;
} & StackProps;

const styles = cssMap({
	container: {
		position: 'relative',
	},
	foldableStyle: {
		display: 'flex',
		flexDirection: 'column',
		borderRadius: token('radius.small'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		textAlign: 'left',
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.raised'),
		gap: token('space.100'),
		transition: 'background-color 0.2s ease',
		width: '100%',
	},
	foldedStyle: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.brand.subtlest.hovered'),
		},
	},
	innerContainer: {
		overflow: 'hidden',
		height: '22px',
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 1,
	},
	contentWrapper: {
		overflow: 'hidden',
		transition: 'max-height 0.3s ease',
	},
	contentExpanded: {
		maxHeight: '500px',
	},
	contentCollapsed: {
		maxHeight: '22px',
	},
	collapseButtonStyle: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
		transition: 'transform 0.3s ease',
	},
	collapseButtonHidden: {
		visibility: 'hidden',
		pointerEvents: 'none',
	},
});

export const DynamicLayoutFoldableSection = ({
	section,
	header,
	content,
	children,
}: FoldedStyleProps) => {
	const [isFolded, setIsFolded] = React.useState<boolean>(section.attributes?.folded === '1');

	const renderContent = () => {
		if (isFolded) {
			return (
				<Pressable
					xcss={cx(styles.foldableStyle, styles.foldedStyle)}
					onClick={() => {
						setIsFolded(false);
					}}
				>
					{header && header.attributes?.show !== 'false' && content && (
						<Text weight="bold">{header && (header.children as string)}</Text>
					)}
					<Box
						xcss={cx(
							styles.contentWrapper,
							isFolded ? styles.contentCollapsed : styles.contentExpanded,
						)}
					>
						<Box xcss={styles.innerContainer}>
							<OneColumnLayout elements={content} />
						</Box>
					</Box>
				</Pressable>
			);
		} else {
			return (
				<Box xcss={cx(styles.foldableStyle)}>
					{header && header.attributes?.show !== 'false' && content && (
						<Text weight="bold">{header && (header.children as string)}</Text>
					)}
					<Box
						xcss={cx(
							styles.contentWrapper,
							isFolded ? styles.contentCollapsed : styles.contentExpanded,
						)}
					>
						{children}
					</Box>
				</Box>
			);
		}
	};

	return (
		<Box xcss={styles.container}>
			<Box xcss={cx(styles.collapseButtonStyle, isFolded && styles.collapseButtonHidden)}>
				<IconButton
					icon={ChevronUpIcon}
					label="Collapse section"
					appearance="subtle"
					spacing="compact"
					onClick={() => setIsFolded(true)}
				/>
			</Box>
			{renderContent()}
		</Box>
	);
};
